import { z } from 'zod';

const soapSchema = z
  .object({
    SubjectiveChiefComplaint: z.string().optional(),
    SubjectiveAnamnesis: z.string().optional(),
    Objective: z.string().optional(),
    Assessment: z.string().optional(),
    Planning: z.string().optional(),
  })
  .passthrough();

const drugRecommendationSchema = z.array(
  z
    .object({
      salesItemId: z.string().optional(),
      salesItemName: z.string().optional(),
    })
    .passthrough(),
);

export const chartSchema = z.object({
  EncounterId: z.string(),
  PatientId: z.string(),
  soap: soapSchema,
  drugRecommendation: drugRecommendationSchema,
});

export type Chart = z.infer<typeof chartSchema>;

const salesItemSchema = z.object({
  salesItemId: z.string(),
  salesItemCode: z.string(),
  salesItemName: z.string(),
  activeIngredientsName: z.string(),
  formularium: z.string(),
  organizationId: z.array(z.string()),
});

export const medicationResponseSchema = z.object({
  status: z.number(),
  data: z.array(salesItemSchema),
  message: z.string(),
});

export type MedicationResponse = z.infer<typeof medicationResponseSchema>;
export const SyncToEhrResponseSchema = z.object({
  company: z.string().nullish(),
  status: z.string().nullish(),
  code: z.number().nullish(),
  message: z.string().nullish(),
  data: z.string().nullish(),
  cp: z.number().nullish(),
  total: z.number().nullish(),
});
export type SyncToEhrResponse = z.infer<typeof SyncToEhrResponseSchema>;
export type SoapData = z.infer<typeof soapSchema>;

const IcdRecommendationItemSchema = z.object({
  id: z.string(),
  label: z.string(),
});
export const IcdRecommendationSchema = z.object({
  status: z.number(),
  data: z.array(IcdRecommendationItemSchema),
});

const DrugRecommendationItemSchema = z.record(z.unknown());

export const DrugRecommendationSchema = z.object({
  status: z.number(),
  data: z.array(DrugRecommendationItemSchema),
});

export const SiloamSyncEhrPayloadSchema = z
  .object({
    EncounterId: z.string(),
    soap: soapSchema,
    icdRecommendation: IcdRecommendationSchema.optional(),
    drugRecommendation: DrugRecommendationSchema.optional(),
  })
  .passthrough();

export type IcdRecommendation = z.infer<typeof IcdRecommendationSchema>;
export type DrugRecommendation = z.infer<typeof DrugRecommendationSchema>;
export type SiloamSyncEhrPayload = z.infer<typeof SiloamSyncEhrPayloadSchema>;

export const SubjectiveItemSchema = z
  .object({
    OrganizationId: z.number().optional(),
    EncounterId: z.string().optional(),
    AdmissionId: z.number().optional(),
    type: z.enum(['PATIENT_COMPLAINT', 'ANAMNESIS']),
    remarks: z.string().nullish(),
    created_date: z.string().optional(),
    modified_date: z.string().optional(),
  })
  .passthrough();

export const SubjectiveJsonSchema = z.array(SubjectiveItemSchema);

export const RemarksJsonSchema = z
  .object({
    OrganizationId: z.number().optional(),
    EncounterId: z.string().optional(),
    AdmissionId: z.number().optional(),
    remarks: z.string().nullish(),
    created_date: z.string().optional(),
    modified_date: z.string().optional(),
  })
  .passthrough();

export const SiloamSignedChartFromApiSchema = z
  .object({
    organizationId: z.number().nullish(),
    encounterId: z.string().nullish(),
    patientId: z.number().nullish(),
    admissionId: z.number().nullish(),
    subjective_json: z.string().nullish(),
    objective_json: z.string().nullish(),
    assessment_json: z.string().nullish(),
    planning_json: z.string().nullish(),
  })
  .passthrough();

export const SiloamSignedChartSchema = z
  .object({
    EncounterId: z.string(),
    soap: soapSchema,
    PatientId: z.string().optional(),
    icdRecommendation: z.array(IcdRecommendationItemSchema).optional(),
    drugRecommendation: z.array(DrugRecommendationItemSchema).optional(),
  })
  .passthrough();

export const ErrorApiResponseSchema = z
  .object({
    company: z.string().nullish(),
    status: z.string().nullish(),
    code: z.number().nullish(),
    message: z.string().nullish(),
    total: z.number().nullish(),
  })
  .passthrough();

export const appointmentSchema = z
  .object({
    id: z.string(),
    name: z.string(),
    startDate: z.string().optional(),
    endDate: z.string().optional(),
    admissionDate: z.string().optional(),
    organizationId: z.number().nullish(),
    organizationName: z.string().nullish(),
    patient: z.object({
      id: z.string(),
      fullName: z.string(),
      birthdate: z.string().optional(),
      gender: z.string().optional(),
    }),
  })
  .passthrough();
export const appointmentsSchema = z.array(appointmentSchema);

// Schema for fetching latest SOAP notes by patient ID
// Response from /latestsoapbypatientid/:patientId endpoint
export const LatestSoapNoteSchema = z
  .object({
    organizationId: z.number(),
    encounterId: z.string(),
    patientId: z.number(),
    admissionId: z.number(),
    doctorId: z.number().nullish(),
    doctorName: z.string().nullish(),
    specialty: z.string().nullish(),
    subjective_json: z.string().nullable(),
    objective_json: z.string().nullable(),
    assessment_json: z.string().nullable(),
    planning_json: z.string().nullable(),
  })
  .passthrough();

export const FetchChartsResponseSchema = z.object({
  data: z.array(LatestSoapNoteSchema),
});

export type LatestSoapNote = z.infer<typeof LatestSoapNoteSchema>;
export type FetchChartsResponse = z.infer<typeof FetchChartsResponseSchema>;
